// Frontend-local stand-in for the workflow-engine contract the real backend
// will implement. That backend hasn't shipped yet, so these enums/shapes do
// NOT exist in @atlas/types — they deliberately mirror the already-confirmed
// shape of the real workflow/hook/checklist/workspace concepts, so swapping
// this module for the real `GET /work-orders/{id}` / `POST /transitions`
// fields later is a single-file change. Do not import these from anywhere
// outside `workflow-stub.ts` and its consumers — once the real backend lands,
// this whole file is deleted in favor of `@atlas/types`.
//
// `WorkflowPhase` is the exception: the real snapshot already carries it, so it
// is re-exported from `@atlas/types` below rather than mirrored.

import { type ValidationLane as ValidationLaneEnum, WorkflowPhase } from '@atlas/types'

/**
 * Where a status sits in the lifecycle. Re-exported from `@atlas/types`, not
 * mirrored: the snapshot carries the backend's own values, and a local copy
 * with different ones compiled into a phase comparison that was never true.
 */
export { WorkflowPhase }

/** Whether a hook runs as a synchronous guard (PRE) or an async action (POST). */
export enum HookStage {
	PRE = 'PRE',
	POST = 'POST',
}

/** Fixed hook vocabulary — code-defined; only ordering and per-transition attachment are admin-configurable data. */
export enum HookCode {
	CHECKLIST_COMPLETE = 'checklist_complete',
	DELIVERY_SLOTS_FILLED = 'delivery_slots_filled',
	ARTIFACT_EXISTS = 'artifact_exists',
	CHANGE_REQUEST_CREATED = 'change_request_created',
	CHANGE_REQUEST_RESOLVED = 'change_request_resolved',
	ASSIGNEE_PRESENT = 'assignee_present',
	FIELDS_PRESENT = 'fields_present',
	DESIGN_DRAFTS_SUBMITTED = 'design_drafts_submitted',
	EMIT_EVENT = 'emit_event',
	NOTIFY = 'notify',
	LOG_MESSAGE = 'log_message',
}

/** The pluggable workspace kinds, one per stage of the confirmed ToBe mockup. */
export enum WorkspaceCode {
	QUEUE_CARD = 'queue_card',
	RESOLUTION = 'resolution',
	PRODUCTION = 'production',
	VALIDATION = 'validation',
	DISTRIBUTION = 'distribution',
	SUMMARY = 'summary',
}

/** One status in the resolved workflow's configured sequence. */
export interface WorkflowStatusSnapshot {
	id: string
	code: string
	label: string
	phase: WorkflowPhase
	position: number
	initial: boolean
	terminal: boolean
	/**
	 * This status's resolved primary surface for the work order's OWN asset
	 * type (design.md D5) — a real `workspace_assignment` row, asset-type
	 * override winning over the status's own default. `null` when neither
	 * exists (misconfiguration) — a caller renders the fail-safe fallback
	 * panel, never guesses.
	 */
	workspace: ResolvedWorkspace | null
}

/** One pre-hook (guard) attached to a transition, in evaluation order. */
export interface TransitionGuard {
	hookCode: HookCode
	/** Guard-specific matcher, e.g. `{ slot: 'visual' }` for delivery_slots_filled. */
	params?: Record<string, unknown>
}

/** A transition legal from the work order's current status, as the CTA bar/drag consume it. */
export interface WorkflowTransitionSnapshot {
	/** The real `workflow_transition` row's own id — the checklist-instance routes (`/transitions/{transitionId}/checklist...`) are keyed by it. */
	transitionId: string
	toStatusId: string
	direction: 'forward' | 'backward'
	ctaLabel: string
	/**
	 * The action bar's supporting copy for this specific transition, confirmed
	 * against the real mockup's own per-transition `hint` text. Optional — the
	 * real `workflow_transition` row has no backing column for this yet
	 * (`ctaLabel` only), so a caller reading real data never gets one.
	 */
	hint?: string
	guards: TransitionGuard[]
	hasChecklist: boolean
	checklistMode?: 'static' | 'dynamic'
}

/**
 * The three sequential validation lanes, as a string-literal union derived
 * from the single source of truth `ValidationLane` enum in `@atlas/types`.
 * Kept as a derived union (rather than re-exporting the enum itself) so every
 * existing consumer that compares/assigns plain string literals (`'internal'`,
 * `'design'`, `'compliance'`) keeps working unchanged.
 */
export type ValidationLane = `${ValidationLaneEnum}`

/**
 * The `GET /work-orders/{id}` workflow-snapshot fields the state timeline and
 * the action bar/drag path both read. `lane` is DERIVED, never a stored field
 * — present only while `currentStatusId` is one of the three validation
 * statuses, so a caller never has to keep it separately in sync with the real
 * status.
 */
export interface ResolvedWorkflowSnapshot {
	workflowId: string
	statuses: readonly WorkflowStatusSnapshot[]
	currentStatusId: string
	legalTransitions: readonly WorkflowTransitionSnapshot[]
	lane: ValidationLane | null
}

/** The resolved primary-surface component + its options — the per-`WorkspaceCode` options shape isn't pinned down yet, so this stays loosely typed until it is. */
export interface ResolvedWorkspace {
	code: WorkspaceCode
	options: Record<string, unknown>
}

/** One checklist item, whether it belongs to a static or a dynamic (change-request) checklist. */
export interface ChecklistItem {
	itemId: string
	label: string
	tag?: string
	required: boolean
	checked: boolean
}

/** A checklist instance's completion state, as the gate modal reads/writes it. */
export interface ChecklistState {
	mode: 'static' | 'dynamic'
	items: ChecklistItem[]
	/** Set only once every required item is checked AND the separate confirmation box is ticked. */
	confirmed: boolean
}

/**
 * One change request raised on a validation lane, persisted server-side as a
 * dynamic checklist instance. A lane holds any number of them, each raised,
 * resolved and deleted independently.
 *
 * Resolution is DERIVED from the items (`isChangeRequestResolved`), never
 * stored, so it cannot drift out of sync with them on a toggle.
 */
export interface ChangeRequest {
	changeRequestId: string
	lane: ValidationLane
	authorId: string | null
	/** Resolved from the user directory; a fallback label when the author is unknown. */
	authorName: string
	/** ISO 8601. */
	createdAt: string
	/** The status code the request sends the order back to — the only one its done ticks can change on. */
	sentBackTo: string
	items: ChangeRequestItem[]
}

/** `checked` is the worker marking the fix done; `verified` is the reviewer confirming it — two separate ticks. */
export interface ChangeRequestItem extends ChecklistItem {
	verified: boolean
}

/** The `POST /transitions` 409 rejection shape — carries a machine-readable `HookCode` so the caller can render "what's missing" instead of a generic error. */
export interface TransitionGuardRejection {
	errorCode: 'ILLEGAL_TRANSITION' | 'GUARD_UNMET'
	hookCode?: HookCode
	message: string
}

export type TransitionAttemptResult = { ok: true } | ({ ok: false } & TransitionGuardRejection)
